import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  Field,
  FieldDescription,
  FieldLabel,
} from 'designSystem/components/Field'
import { Input } from 'designSystem/components/Input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from 'designSystem/components/InputGroup'
import { Item, ItemActions, ItemContent } from 'designSystem/components/Item'
import { Separator } from 'designSystem/components/Separator'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import { Textarea } from 'designSystem/components/Textarea'
import {
  type DocChatDraftCitation,
  type DocChatResponseCitations,
  sanitizeDocChatEvalCaseName,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/buildDocChatEvalCase'
import { X } from 'lucide-react'
import React, { type KeyboardEvent, useId, useState } from 'react'
import styled from 'styled-components'

import { DocChatEvalCaseCitations } from './DocChatEvalCaseCitations'
import { InlineTurnCard } from './docChatStyles'
import type { PreviewDocumentHandler } from './previewDocumentHandler'

const CaptureDetails = styled.dl`
  display: grid;
  gap: 0.25rem 1rem;
  grid-template-columns: max-content minmax(0, 1fr);
  margin: 0;

  dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`

const StatementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const WrappingText = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`

function CaptureDetail({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>
        <Text variant='caption' color='muted'>
          {label}
        </Text>
      </dt>
      <dd>
        <Text variant='caption'>{value}</Text>
      </dd>
    </>
  )
}

function ReadOnlyField({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Field>
      <Text as='h4' variant='label'>
        {label}
      </Text>
      {children}
    </Field>
  )
}

function TextField({
  label,
  value,
  disabled,
  description,
  multiline,
  onChange,
}: {
  label: string
  value: string
  disabled: boolean
  description?: string
  multiline?: boolean
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {multiline ? (
        <Textarea
          id={id}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input
          id={id}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </Field>
  )
}

function StatementListField({
  label,
  items,
  disabled,
  onChange,
}: {
  label: string
  items: string[]
  disabled: boolean
  onChange: (items: string[]) => void
}) {
  const { t } = useLingui()
  const id = useId()
  const [draft, setDraft] = useState('')

  const add = () => {
    const value = draft.trim()
    if (!value) return
    onChange([...items, value])
    setDraft('')
  }

  const addOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    add()
  }

  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {items.length ? (
        <StatementList>
          {items.map((statement, index) => (
            <Item key={`${statement}-${index}`} variant='outline' size='xs'>
              <ItemContent>
                <WrappingText>
                  <Text variant='caption'>{statement}</Text>
                </WrappingText>
              </ItemContent>
              <ItemActions variant='trailing'>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon-sm'
                  aria-label={t`Remove`}
                  title={t`Remove`}
                  disabled={disabled}
                  onClick={() => onChange(items.filter((_, i) => i !== index))}>
                  <X />
                </Button>
              </ItemActions>
            </Item>
          ))}
        </StatementList>
      ) : null}
      <InputGroup data-disabled={disabled || undefined}>
        <InputGroupInput
          id={id}
          value={draft}
          disabled={disabled}
          placeholder={t`Judge criterion`}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={addOnEnter}
        />
        <InputGroupAddon align='inline-end'>
          <InputGroupButton
            variant='secondary'
            disabled={disabled || !draft.trim()}
            onClick={add}>
            {t`Add`}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </Field>
  )
}

/** Inline in the chat turn so the document preview stays visible. */
export function DocChatEvalCaseReview({
  caseName,
  query,
  answer,
  siteId,
  orgId,
  sessionId,
  capturedBy,
  citations,
  mustState,
  mustNotState,
  onCaseNameChange,
  onAnswerChange,
  onMustStateChange,
  onMustNotStateChange,
  response,
  onAddCitation,
  onRemoveCitation,
  onPreviewDocument,
  busy,
  error,
  onCancel,
  onSave,
}: {
  caseName: string
  query: string
  answer: string
  siteId: string
  orgId: string
  sessionId: string | null
  capturedBy: string
  citations: DocChatDraftCitation[]
  mustState: string[]
  mustNotState: string[]
  onCaseNameChange: (value: string) => void
  onAnswerChange: (value: string) => void
  onMustStateChange: (items: string[]) => void
  onMustNotStateChange: (items: string[]) => void
  response?: DocChatResponseCitations
  onAddCitation: (citation: DocChatDraftCitation) => void
  onRemoveCitation: (citation: DocChatDraftCitation) => void
  onPreviewDocument?: PreviewDocumentHandler
  busy: boolean
  error: string | null
  onCancel: () => void
  onSave: () => void
}): React.ReactElement {
  const { t } = useLingui()
  const titleId = useId()
  const canSave = Boolean(caseName.trim() && query.trim() && answer.trim())
  return (
    <InlineTurnCard role='region' aria-labelledby={titleId}>
      <Box display='flex' direction='column' gap='xs'>
        <Text as='h3' id={titleId} variant='label'>
          {t`Draft eval`}
        </Text>
        <Text as='p' variant='caption' color='muted'>
          {t`Check the query, answer, checks, and cited documents before this case is saved.`}
        </Text>
      </Box>
      <Separator />
      <TextField
        label={t`Name`}
        value={caseName}
        disabled={busy}
        description={t`Use lowercase letters, numbers, hyphens, and underscores.`}
        onChange={(value) =>
          onCaseNameChange(sanitizeDocChatEvalCaseName(value))
        }
      />
      <ReadOnlyField label={t`Query`}>
        <Item variant='muted' size='xs'>
          <WrappingText>
            <Text variant='body'>{query}</Text>
          </WrappingText>
        </Item>
      </ReadOnlyField>
      <TextField
        label={t`Answer`}
        value={answer}
        disabled={busy}
        multiline
        onChange={onAnswerChange}
      />
      <StatementListField
        label={t`Must state`}
        items={mustState}
        disabled={busy}
        onChange={onMustStateChange}
      />
      <StatementListField
        label={t`Must not state`}
        items={mustNotState}
        disabled={busy}
        onChange={onMustNotStateChange}
      />
      <DocChatEvalCaseCitations
        citations={citations}
        response={response}
        disabled={busy}
        onAdd={onAddCitation}
        onRemove={onRemoveCitation}
        onPreviewDocument={onPreviewDocument}
      />
      <Separator />
      <ReadOnlyField label={t`Captured from`}>
        <CaptureDetails>
          <CaptureDetail
            label={t`Conversation`}
            value={sessionId ?? t`No conversation id`}
          />
          <CaptureDetail label={t`Site`} value={siteId} />
          <CaptureDetail label={t`Organization`} value={orgId} />
          <CaptureDetail label={t`Created by`} value={capturedBy} />
        </CaptureDetails>
      </ReadOnlyField>
      {error ? (
        <Text as='p' variant='caption' color='destructive'>
          {error}
        </Text>
      ) : null}
      <Box display='flex' justify='end' gap='sm'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          disabled={busy}
          onClick={onCancel}>
          {t`Cancel`}
        </Button>
        <Button
          type='button'
          size='sm'
          disabled={busy || !canSave}
          onClick={onSave}>
          {busy && <Spinner size={12} />}
          {busy ? t`Saving...` : t`Save eval case`}
        </Button>
      </Box>
    </InlineTurnCard>
  )
}
